/**
 * User-facing copy for map failure states (#70).
 *
 * Its own module, with no imports, so the strings can be asserted without
 * loading MapPage — which transitively pulls in axios and the rest of the app.
 * Copy is also the thing most likely to be edited by someone who is not
 * thinking about the map's internals, and a file this small is hard to get
 * wrong.
 */

/**
 * Shown when the map will not load, whatever the cause.
 *
 * One message for every failure. Whether it is a missing token, an expired one,
 * or the provider being down is a distinction only we can act on, and the
 * provider's own error text is not written for end users. The previous copy
 * named a build variable and instructed the reader to set it — something only a
 * developer could do. A token missing from the Render frontend build or from
 * react-app/.env.production.local would have shown that to every beta tester on
 * the app's home screen.
 *
 * Never put an environment-variable name, a provider name or a raw error string
 * in here. `utils/honestCopy.test.js` fails the build if a REACT_APP_* name
 * reaches rendered copy, and `mapCopy.test.js` pins the rest.
 */
export const MAP_UNAVAILABLE_MESSAGE =
  "We couldn't load the map right now. Everything else still works — your collection, wishlist, trades and chat are all open.";

/** Heading above that message. */
export const MAP_UNAVAILABLE_TITLE = "Map unavailable";

/**
 * The same card when the server cannot be reached at all (B-27): the device
 * reports no network, or both discovery searches got no answer. The message
 * above promises that collection, wishlist, trades and chat still work, and
 * offline none of them load.
 */
export const MAP_OFFLINE_MESSAGE =
  "We can't connect right now, so the map can't load. Check your internet connection, then tap Retry.";
